/**
 * Debounce utility to prevent rapid function calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @param {Object} options - Options object
 * @param {boolean} options.leading - Execute on leading edge
 * @param {boolean} options.trailing - Execute on trailing edge
 * @returns {Function} Debounced function
 */
export function debounce(func, wait, options = {}) {
  let timeout;
  let result;
  const { leading = false, trailing = true } = options;

  const debounced = function (...args) {
    const callNow = leading && !timeout;

    clearTimeout(timeout);

    timeout = setTimeout(() => {
      timeout = null;
      if (trailing && !leading) {
        result = func.apply(this, args);
      }
    }, wait);

    if (callNow) {
      result = func.apply(this, args);
    }

    return result;
  };

  debounced.cancel = function () {
    clearTimeout(timeout);
    timeout = null;
  };

  debounced.flush = function () {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
      result = func.apply(this, arguments);
    }
    return result;
  };

  return debounced;
}

/**
 * Throttle utility to limit function calls
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(func, limit) {
  let inThrottle;

  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Debounce state updates to prevent rapid re-renders
 * @param {Function} setState - State setter function
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced state setter
 */
export function debounceState(setState, delay = 100) {
  return debounce(setState, delay, { leading: false, trailing: true });
}

/**
 * Create a debounced version of console.log to prevent log spam
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced console.log
 */
export function debouncedLog(delay = 500) {
  const logCache = new Map();

  return (message, ...args) => {
    const key = message + JSON.stringify(args);

    if (logCache.has(key)) {
      clearTimeout(logCache.get(key));
    }

    logCache.set(
      key,
      setTimeout(() => {
        console.log(message, ...args);
        logCache.delete(key);
      }, delay)
    );
  };
}

/**
 * Debounce API calls to prevent duplicate requests
 * @param {Function} apiCall - API function to debounce
 * @param {number} delay - Delay in milliseconds
 * @param {Function} keyGenerator - Function to generate cache key
 * @returns {Function} Debounced API call
 */
export function debounceApiCall(
  apiCall,
  delay = 300,
  keyGenerator = (...args) => JSON.stringify(args)
) {
  const callCache = new Map();

  return async (...args) => {
    const key = keyGenerator(...args);

    // If there's a pending call with the same key, return its promise
    if (callCache.has(key)) {
      return callCache.get(key);
    }

    // Create a new promise for this call
    const promise = new Promise((resolve, reject) => {
      const timeout = setTimeout(async () => {
        try {
          const result = await apiCall(...args);
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          callCache.delete(key);
        }
      }, delay);

      // Store the timeout so we can clear it if needed
      promise.timeoutId = timeout;
    });

    callCache.set(key, promise);
    return promise;
  };
}

/**
 * Create a debounced session state handler
 * @param {Function} onSessionChange - Function to call when session changes
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced session handler
 */
export function createDebouncedSessionHandler(onSessionChange, delay = 200) {
  let lastSessionState = undefined;
  let pendingTimeout = null;

  return (newSession) => {
    // Clear any pending timeout
    if (pendingTimeout) {
      clearTimeout(pendingTimeout);
    }

    // If the session state hasn't actually changed, don't process
    const sessionState =
      newSession === undefined
        ? "undefined"
        : newSession
          ? "authenticated"
          : "unauthenticated";
    if (sessionState === lastSessionState) {
      return;
    }

    // Debounce the session change
    pendingTimeout = setTimeout(() => {
      lastSessionState = sessionState;
      onSessionChange(newSession);
      pendingTimeout = null;
    }, delay);
  };
}
